/**
 * A responsive `<picture>` for the landing page's generated artwork.
 *
 * `scripts/generate_landing_art.py` writes AVIF and WebP at every declared
 * width and a JPEG at the largest one only, so the element offers modern
 * formats first and falls back to one file that always decodes. Explicit
 * `width`/`height` are non-negotiable here: this page is mostly imagery and
 * without intrinsic dimensions every section would shift as art arrives.
 */

interface ArtProps {
  /** Path stem under /public, e.g. "/landing/hero-wide" or "/posters/night-kitchen". */
  base: string;
  /** Delivery widths, largest first. Omit for single-size art such as posters. */
  widths?: number[];
  /** Intrinsic size of the largest rendition, used for the aspect ratio. */
  width: number;
  height: number;
  /** Empty string marks the image decorative; anything else must describe it. */
  alt: string;
  sizes?: string;
  className?: string;
  /** The hero only. Everything else is below the fold and lazy. */
  priority?: boolean;
}

function srcset(base: string, widths: number[] | undefined, extension: string): string {
  if (!widths) return `${base}.${extension}`;
  return widths.map((width) => `${base}-${width}.${extension} ${width}w`).join(", ");
}

/**
 * React 18 does not recognise the camelCase `fetchPriority` prop and warns
 * about it, so the lowercase attribute the HTML spec actually defines is
 * spread in instead. A `Record<string, string>` is what makes that type-check.
 */
const HIGH_PRIORITY: Record<string, string> = { fetchpriority: "high" };

export function Art({
  base,
  widths,
  width,
  height,
  alt,
  sizes,
  className,
  priority = false,
}: ArtProps) {
  const fallback = widths ? `${base}-${widths[0]}.jpg` : `${base}.jpg`;
  return (
    <picture>
      <source type="image/avif" srcSet={srcset(base, widths, "avif")} sizes={sizes} />
      <source type="image/webp" srcSet={srcset(base, widths, "webp")} sizes={sizes} />
      <img
        {...(priority ? HIGH_PRIORITY : {})}
        src={fallback}
        width={width}
        height={height}
        alt={alt}
        className={className}
        loading={priority ? "eager" : "lazy"}
        decoding={priority ? "sync" : "async"}
      />
    </picture>
  );
}
